import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MailIcon, ChevronDownIcon } from 'lucide-react';
import { CONTACT_EMAIL, PAYMENT_OPTIONS, PREORDER_GROUPS, PREORDER_ITEMS } from '../data/bookingConfig';
const inputClass = 'w-full bg-white border border-ink/20 px-4 py-3 font-sans text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:border-ink transition-colors duration-200';
const labelClass = 'block mb-2 font-sans text-[10px] tracking-[0.2em] uppercase text-ink/55';
export function PreorderSection() {
  return <section id="forbestallning" className="w-full px-6 py-24 md:py-32 border-t border-ink/10" aria-labelledby="forbestallning-rubrik" data-id="element-11" data-mp="37sx9exqz35zi">
      <div className="max-w-2xl mx-auto" data-id="element-12" data-mp="32srb4yq76l8g">
        <div className="mb-12 md:mb-16" data-id="element-13" data-mp="n2od154j6xtb">
          <h2 id="forbestallning-rubrik" className="font-sans text-5xl md:text-7xl font-medium tracking-[-0.03em] uppercase leading-[0.9] text-ink" data-id="element-14" data-mp="n1d8h339hm15">
            Förbeställ
          </h2>
          <p className="mt-6 font-serif italic text-2xl md:text-3xl font-light text-ink/80" data-id="element-15" data-mp="2ci5xe1cim7s5">
            Mackor och våra signaturer.
          </p>
          <p className="mt-6 font-sans text-sm leading-relaxed text-ink/55" data-id="element-16" data-mp="2lghveofpo7um">
            Klicka på en produkt för att se vad den innehåller. Din förfrågan
            skickas som ett färdigt mejl från din egen e-postadress till{' '}
            {CONTACT_EMAIL} — bekräftelse kommer som svar.
          </p>
        </div>

        <PreorderForm data-id="element-17" data-mp="1xrymq7sr1t20" />
      </div>
    </section>;
}
function PreorderForm() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [payment, setPayment] = useState<string>(PAYMENT_OPTIONS[0].value);
  const [notes, setNotes] = useState('');
  const [invoiceCompany, setInvoiceCompany] = useState('');
  const [invoiceOrgNr, setInvoiceOrgNr] = useState('');
  const [invoiceAddress, setInvoiceAddress] = useState('');
  const [invoiceZip, setInvoiceZip] = useState('');
  const [invoiceCity, setInvoiceCity] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [invoiceReference, setInvoiceReference] = useState('');
  const isInvoice = payment === 'Faktura';
  const total = useMemo(() => Object.values(quantities).reduce((sum, n) => sum + n, 0), [quantities]);
  const setQty = (item: string, value: number) => setQuantities(prev => ({
    ...prev,
    [item]: Math.max(0, value)
  }));
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const order = PREORDER_ITEMS.filter(item => (quantities[item.name] ?? 0) > 0).map(item => `  • ${item.name} — ${quantities[item.name]} st`).join('\n');
    const invoiceBlock = isInvoice ? ['', 'Fakturauppgifter:', `  Företag/namn: ${invoiceCompany}`, `  Org.nr / personnr: ${invoiceOrgNr}`, `  Fakturaadress: ${invoiceAddress}`, `  Postnr & ort: ${invoiceZip} ${invoiceCity}`, `  Faktura-e-post: ${invoiceEmail}`, `  Referens: ${invoiceReference || '—'}`] : [];
    const body = ['FÖRBESTÄLLNING — LILLA VERKET', '', `Namn: ${name}`, `Telefon: ${phone}`, '', 'Beställning:', order, `Totalt: ${total} st`, '', `Hämtas: ${date} kl. ${time}`, `Betalning: ${payment}`, ...invoiceBlock, '', `Övrigt: ${notes || '—'}`, '', 'Skickat via lillaverket.se'].join('\n');
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Förbeställning — ${date} kl. ${time}`)}&body=${encodeURIComponent(body)}`;
  };
  return <form onSubmit={handleSubmit} className="space-y-7" data-id="element-18" data-mp="xn0rxctv5ge3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-19" data-mp="355k7jortem24">
        <div data-id="element-20" data-mp="305xsrawc8xr7">
          <label className={labelClass} htmlFor="fb-namn" data-id="element-21" data-mp="305enourse3ul">
            Namn
          </label>
          <input id="fb-namn" required value={name} onChange={e => setName(e.target.value)} className={inputClass} placeholder="För- och efternamn" data-id="element-22" data-mp="2w3kqiwgsvhz6" />
        </div>
        <div data-id="element-23" data-mp="3nuh27tr4jgkv">
          <label className={labelClass} htmlFor="fb-tel" data-id="element-24" data-mp="3nxd3ns26kne2">
            Telefon
          </label>
          <input id="fb-tel" required type="tel" value={phone} onChange={e => setPhone(e.target.value)} className={inputClass} placeholder="07X XXX XX XX" data-id="element-25" data-mp="3jvrqe5lqi73c" />
        </div>
      </div>

      <fieldset data-id="element-26" data-mp="bfeqs6q0o16o">
        <legend className={labelClass} data-id="element-27" data-mp="bfyde0qm3dr1">Vad vill du förbeställa?</legend>
        <div className="space-y-6" data-id="element-28" data-mp="6mdc9ztq23nm">
          {PREORDER_GROUPS.map(group => <div key={group.title} data-id="element-29" data-mp="dzbh7qarvcyf">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2" data-id="element-30" data-mp="e04hq9j51684">
                <h3 className="font-serif text-xl font-medium text-ink" data-id="element-31" data-mp="ixwgnni79slt">
                  {group.title}
                </h3>
                {group.note && <p className="font-sans text-[11px] leading-relaxed text-ink/45" data-id="element-32" data-mp="rykmvw1cqoo5">
                    {group.note}
                  </p>}
              </div>
              <div className="border border-ink/15 divide-y divide-ink/10" data-id="element-33" data-mp="11w8tbj8blkm5">
                {group.items.map(item => {
              const isOpen = expanded === item.name;
              return <div key={item.name} data-id="element-34" data-mp="1blscyr3uxii2">
                <div className="flex items-center justify-between gap-4 px-4 py-3" data-id="element-35" data-mp="1bkqnviq0avhc">
                  <button type="button" onClick={() => setExpanded(isOpen ? null : item.name)} aria-expanded={isOpen} className="group flex items-center gap-3 text-left" data-id="element-36" data-mp="1foeyka9xq76t">
                    {item.image && <span className="w-10 h-10 shrink-0 overflow-hidden rounded-sm bg-ink/5" data-id="element-37" data-mp="3cpazko7gze8p">
                        <img src={item.image.src} alt="" aria-hidden="true" loading="lazy" className="w-full h-full object-cover" style={{
                        objectPosition: item.image.focus ?? 'center',
                        transform: item.image.zoom ? `scale(${item.image.zoom})` : undefined
                      }} draggable={false} data-id="element-38" data-mp="3qgptvr6tv1v2" />
                      </span>}
                    <span className="font-sans text-sm text-ink/90 group-hover:text-ink" data-id="element-39" data-mp="2a1y29ghi9ymt">
                      {item.name}
                    </span>
                    <ChevronDownIcon strokeWidth={1.5} className={`w-3.5 h-3.5 text-ink/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-40" data-mp="pk5nj7sttt44" />
                  </button>
                  <div className="flex items-center gap-3 shrink-0" data-id="element-41" data-mp="kquf528d94a3">
                    <button type="button" onClick={() => setQty(item.name, (quantities[item.name] ?? 0) - 1)} className="w-8 h-8 border border-ink/20 text-ink/60 hover:border-ink hover:text-ink transition-colors duration-200" aria-label={`Minska antal ${item.name}`} data-id="element-42" data-mp="6rupbr94ajcc">
                      −
                    </button>
                    <span className="w-6 text-center font-sans text-sm tabular-nums text-ink" data-id="element-43" data-mp="1w8capefsmxlj">
                      {quantities[item.name] ?? 0}
                    </span>
                    <button type="button" onClick={() => setQty(item.name, (quantities[item.name] ?? 0) + 1)} className="w-8 h-8 border border-ink/20 text-ink/60 hover:border-ink hover:text-ink transition-colors duration-200" aria-label={`Öka antal ${item.name}`} data-id="element-44" data-mp="k0bfxkreyykw">
                      +
                    </button>
                  </div>
                </div>
                <AnimatePresence initial={false} data-id="element-45" data-mp="3nexpu3vc9gwv">
                  {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.25,
                    ease: [0.22, 1, 0.36, 1]
                  }} className="overflow-hidden" data-id="element-46" data-mp="3nh0n98ytmcbm">
                      <p className="px-4 pb-4 font-sans text-xs leading-relaxed text-ink/55" data-id="element-47" data-mp="1p58og6nrftp6">
                        {item.ingredients}
                      </p>
                    </motion.div>}
                </AnimatePresence>
                    </div>;
            })}
              </div>
            </div>)}
        </div>
        <p className="mt-3 font-sans text-[11px] text-ink/50" data-id="element-48" data-mp="167o0r2wqf165">
          Totalt {total} st
        </p>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-49" data-mp="1a997s7ml4l8f">
        <div data-id="element-50" data-mp="1f71a7vkuvpqv">
          <label className={labelClass} htmlFor="fb-datum" data-id="element-51" data-mp="1f7ayneq95oug">
            Hämtas — dag
          </label>
          <input id="fb-datum" required type="date" value={date} onChange={e => setDate(e.target.value)} className={inputClass} data-id="element-52" data-mp="1k7oyi9hp9she" />
        </div>
        <div data-id="element-53" data-mp="2vk9czragh9lq">
          <label className={labelClass} htmlFor="fb-tid" data-id="element-54" data-mp="2vnx3ehkltfw8">
            Hämtas — tid
          </label>
          <input id="fb-tid" required type="time" value={time} onChange={e => setTime(e.target.value)} className={inputClass} data-id="element-55" data-mp="xky9k76nsg9t" />
        </div>
      </div>

      <fieldset data-id="element-56" data-mp="1beyy683wcmwg">
        <legend className={labelClass} data-id="element-57" data-mp="1bde6qrcvpk2t">Betalning</legend>
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-58" data-mp="eo6ivcoseyvq">
          {PAYMENT_OPTIONS.map(option => <label key={option.value} className={`flex-1 flex items-center gap-3 px-4 py-3 border cursor-pointer transition-colors duration-200 ${payment === option.value ? 'border-ink' : 'border-ink/20 hover:border-ink/50'}`} data-id="element-59" data-mp="jn9l6pddur00">
              <input type="radio" name="betalning" value={option.value} checked={payment === option.value} onChange={() => setPayment(option.value)} className="accent-ink" data-id="element-60" data-mp="1z306bfx5y1bc" />
              <span className="font-sans text-sm text-ink/90" data-id="element-61" data-mp="297oudnvtlzk0">
                {option.label}
              </span>
            </label>)}
        </div>
      </fieldset>

      <AnimatePresence initial={false} data-id="element-62" data-mp="20fwpdspkfimt">
        {isInvoice && <motion.fieldset initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className="overflow-hidden" data-id="element-63" data-mp="20fenafg82a6z">
            <legend className={labelClass} data-id="element-64" data-mp="1lintclpbhqor">Fakturauppgifter</legend>
            <div className="space-y-5 border border-ink/15 p-5" data-id="element-65" data-mp="1hhu51r7yq9vc">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-66" data-mp="3fw59hnne7l9r">
                <div data-id="element-67" data-mp="3fuaxwzy0h5ic">
                  <label className={labelClass} htmlFor="fk-foretag" data-id="element-68" data-mp="3butqi0z0xyaj">
                    Företag / namn
                  </label>
                  <input id="fk-foretag" required value={invoiceCompany} onChange={e => setInvoiceCompany(e.target.value)} className={inputClass} placeholder="Företagsnamn" data-id="element-69" data-mp="197wm4da347iy" />
                </div>
                <div data-id="element-70" data-mp="149bh80jw0zo6">
                  <label className={labelClass} htmlFor="fk-orgnr" data-id="element-71" data-mp="1rymn1wg3hk96">
                    Org.nr / personnr
                  </label>
                  <input id="fk-orgnr" required value={invoiceOrgNr} onChange={e => setInvoiceOrgNr(e.target.value)} className={inputClass} placeholder="XXXXXX-XXXX" data-id="element-72" data-mp="1wxz8ccrbij9n" />
                </div>
              </div>
              <div data-id="element-73" data-mp="7kvqw28yema0">
                <label className={labelClass} htmlFor="fk-adress" data-id="element-74" data-mp="chvzokx6u34t">
                  Fakturaadress
                </label>
                <input id="fk-adress" required value={invoiceAddress} onChange={e => setInvoiceAddress(e.target.value)} className={inputClass} placeholder="Gatuadress" data-id="element-75" data-mp="40c14jc2x1d1" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-76" data-mp="90zuvcrozway">
                <div data-id="element-77" data-mp="3hfi4hixa7jwo">
                  <label className={labelClass} htmlFor="fk-postnr" data-id="element-78" data-mp="3hgta7oaaf1qf">
                    Postnummer
                  </label>
                  <input id="fk-postnr" required value={invoiceZip} onChange={e => setInvoiceZip(e.target.value)} className={inputClass} placeholder="XXX XX" data-id="element-79" data-mp="3ci9nvmrs7nxj" />
                </div>
                <div data-id="element-80" data-mp="2yn7ayfoioyb8">
                  <label className={labelClass} htmlFor="fk-ort" data-id="element-81" data-mp="2tqgs5klbvom4">
                    Ort
                  </label>
                  <input id="fk-ort" required value={invoiceCity} onChange={e => setInvoiceCity(e.target.value)} className={inputClass} placeholder="Ort" data-id="element-82" data-mp="1wzler2nj8e6r" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-83" data-mp="2arrtqley8f4m">
                <div data-id="element-84" data-mp="2fq37h78d79s4">
                  <label className={labelClass} htmlFor="fk-epost" data-id="element-85" data-mp="2fos0yznjbnxb">
                    Faktura-e-post
                  </label>
                  <input id="fk-epost" required type="email" value={invoiceEmail} onChange={e => setInvoiceEmail(e.target.value)} className={inputClass} placeholder="faktura@företag.se" data-id="element-86" data-mp="2kq7mstxjdygr" />
                </div>
                <div data-id="element-87" data-mp="zbjzr8dl2c4k">
                  <label className={labelClass} htmlFor="fk-referens" data-id="element-88" data-mp="zd4lc8hx6kqa">
                    Referens (valfritt)
                  </label>
                  <input id="fk-referens" value={invoiceReference} onChange={e => setInvoiceReference(e.target.value)} className={inputClass} placeholder="Kostnadsställe, beställare…" data-id="element-89" data-mp="n0ajf2qlak9f" />
                </div>
              </div>
            </div>
          </motion.fieldset>}
      </AnimatePresence>

      <div data-id="element-90" data-mp="15sd2slum8end">
        <label className={labelClass} htmlFor="fb-ovrigt" data-id="element-91" data-mp="15rtnlcwco4d1">
          Övrigt
        </label>
        <textarea id="fb-ovrigt" rows={3} value={notes} onChange={e => setNotes(e.target.value)} className={inputClass} placeholder="Allergier, önskemål…" data-id="element-92" data-mp="11odpec4mtvy8" />
      </div>

      <button type="submit" className="w-full inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-ink text-white font-sans text-[11px] md:text-xs tracking-[0.2em] uppercase hover:bg-ink/85 transition-colors duration-200" data-id="element-93" data-mp="3mdl2jczwmbbm">
        <MailIcon strokeWidth={1.5} className="w-4 h-4" data-id="element-94" data-mp="3nwl1c2z71tws" />
        Skicka förbeställning
      </button>
    </form>;
}